import React, { useEffect, useState } from "react";
import { Sparkles, GraduationCap, Quote } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CtaButton } from "@/components/CtaButton";
import { GhlEmbedPlaceholder } from "@/components/GhlEmbedPlaceholder";

export default function AboutPage() {
    const [p, setP] = useState(null);
    useEffect(() => {
        api.practitioner().then(setP).catch(() => {});
    }, []);

    return (
        <div>
            <PageHeader
                eyebrow="OUR PRACTITIONER"
                title="Meet Riley Ann"
                subtitle="A Certified Clinical Ayurvedic Practitioner blending classical lineage with grounded, modern care — from her clinic on the slopes of Haleakalā."
                breadcrumbs={[{ label: "Home", to: "/" }, { label: "About" }]}
            />

            <section className="py-12 sm:py-16">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-5">
                            <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-border/70 shadow-[0_24px_60px_rgba(30,35,32,0.18)] bg-sand">
                                {p?.portrait_url && (
                                    <img
                                        src={p.portrait_url}
                                        alt={p.name}
                                        className="h-full w-full object-cover"
                                    />
                                )}
                            </div>
                            <div className="mt-6 rounded-2xl border border-border/80 bg-white/80 p-6">
                                <p className="font-accent text-xs text-terracotta">CREDENTIALS</p>
                                <ul className="mt-3 space-y-2 text-sm text-foreground/80">
                                    {p?.credentials?.map((c, i) => (
                                        <li key={`cred-${i}-${c.slice(0, 20)}`} className="flex items-start gap-2">
                                            <GraduationCap className="mt-0.5 h-4 w-4 text-moss shrink-0" strokeWidth={1.75} />
                                            <span>{c}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="lg:col-span-7">
                            <article className="prose-ay max-w-[65ch]">
                                <h2 className="font-display text-2xl sm:text-3xl text-moss">
                                    A personal path into Ayurveda
                                </h2>
                                <p>{p?.story}</p>

                                <h3 className="font-display text-xl text-moss">My approach</h3>
                                <p>{p?.approach}</p>
                            </article>

                            {p?.pull_quote && (
                                <figure className="my-10 rounded-3xl bg-sand/60 border border-border/70 p-6 sm:p-8 relative">
                                    <Quote className="absolute -top-3 -left-3 h-8 w-8 text-terracotta bg-cream rounded-full p-1 border border-border/70" strokeWidth={1.5} />
                                    <blockquote className="font-display text-xl sm:text-2xl text-moss leading-snug">
                                        “{p.pull_quote}”
                                    </blockquote>
                                    <figcaption className="mt-4 text-sm text-foreground/65">
                                        — {p.name}, {p.title}
                                    </figcaption>
                                </figure>
                            )}

                            <div>
                                <p className="font-accent text-xs text-terracotta">SPECIALIZATIONS</p>
                                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm text-foreground/85">
                                    {p?.specializations?.map((s, i) => (
                                        <li key={`spec-${i}-${s.slice(0, 20)}`} className="flex items-start gap-2 rounded-xl border border-border/70 bg-white/70 p-3">
                                            <Sparkles className="mt-0.5 h-4 w-4 text-sage shrink-0" strokeWidth={1.75} />
                                            <span>{s}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="mt-8">
                                <p className="font-accent text-xs text-terracotta">WHAT TO EXPECT</p>
                                <ol className="mt-3 space-y-3 text-sm text-foreground/85">
                                    {[
                                        ["You share your story.", "In our 2-hour intake, we talk through your full health history, current concerns, and goals."],
                                        ["Riley listens — then maps your unique constitution.", "Using pulse, tongue, and conversation, we identify your prakruti (nature) and vikruti (current imbalance)."],
                                        ["You leave with a written plan.", "A clear, personalised set of diet, lifestyle, herb, and bodywork recommendations — nothing generic."],
                                        ["We refine together.", "Follow-ups every few weeks to track progress, troubleshoot, and adjust as you change."],
                                    ].map(([title, body], i) => (
                                        <li key={title} className="rounded-xl border border-border/70 bg-white/70 p-4 flex gap-3">
                                            <span className="shrink-0 h-7 w-7 rounded-full bg-sand text-moss font-display text-sm inline-flex items-center justify-center">
                                                {i + 1}
                                            </span>
                                            <div>
                                                <p className="font-medium text-foreground/90">{title}</p>
                                                <p className="mt-1 text-foreground/75 leading-relaxed">{body}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            </div>

                            <div className="mt-8">
                                <CtaButton to="/contact">Request a consultation</CtaButton>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-12 sand-wash bg-grain">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                        <div className="lg:col-span-5">
                            <h3 className="font-display text-2xl text-moss">Ready when you are.</h3>
                            <p className="mt-2 text-foreground/75">
                                Tell Riley a bit about you and she’ll personally reach out to set up your first conversation.
                            </p>
                        </div>
                        <div className="lg:col-span-7">
                            <GhlEmbedPlaceholder kind="consultation-form" />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
